
import { IFandfPensionInputs } from 'app/calcs/customers/fandf/interfaces'
import { IIOPensionInputs } from 'app/calcs/customers/IO/interfaces'
import { ILocal145PensionInputs } from 'app/calcs/customers/local145/interfaces'
import { ILocal697PensionInputs } from 'app/calcs/customers/local697/interfaces'
import { ILocal701PensionInputs } from 'app/calcs/customers/local701/interfaces'
import { ILocal705PensionInputs } from 'app/calcs/customers/local705/interfaces'
import { INcpersPensionInputs } from 'app/calcs/customers/ncpers/current'
import { INcpersPriorPensionInputs } from 'app/calcs/customers/ncpers/prior'
import { INEBFPensionInputs } from 'app/calcs/customers/NEBF/interfaces'
import { ISharedPensionInputs } from 'app/calcs/customers/shared/interfaces';
import { ExpenseMap, personalExpenseFieldNames } from 'app/calcs/expenses/interfaces'
import { ISpousalPensionInputs } from 'app/calcs/pensions/spousal'
import { defaultRatesOfReturn, ensureRetirementSavingsPersonInputs, IRatesOfReturn, IRetirementSavingsPersonInputs } from 'app/calcs/retirementSavings'
import { ISSAInformation } from 'app/calcs/ssa'
import { AutoOwnership, Gender, Guid, IIndvidualRetirementExpense, IRetirementExpense, RetirementUnits, SectionType } from 'app/redux/Profile/model/interfaces'
import { ISerializable } from 'app/services/ISerializable'
import { LifeExpectancyService } from 'app/services/LifeExpectancyService'
import * as utils from 'app/utils'
import { isGoodMoment } from 'app/utils/DateCalcs'
import { configName } from 'config'
import * as ETL697 from 'config/697/ETL'
import * as ETL701 from 'config/701/ETL'
import * as ETL705 from 'config/705/ETL'
import * as ETL71 from 'config/71/ETL'
import * as ETL145 from 'config/145/ETL'
import * as ETLFandf from 'config/fandf/ETL'
import * as _ from 'lodash'
import * as moment from 'moment'

interface IAutoExpense {
  name: string
  loanPayments: number
  fuelCosts: number
  insurance: number
  autoMaintenance: number
  ownership: AutoOwnership
}

interface IExpenses {
  estimatedMonthlyExpenses?: number
  estimatedMonthlyExpensesPostSwitchover?: number
  expenseSwitchoverMemberAge: moment.Duration
  auto: IAutoExpense[]
  mortgage: IRetirementExpense
  charity: IRetirementExpense
  propertyTaxes: IRetirementExpense
  taxes: IRetirementExpense
  savingsAndDebt: IRetirementExpense
  homeInsurance: IRetirementExpense
  homeMaintenance: IRetirementExpense
  homeUtilities: IRetirementExpense
  groceries: IRetirementExpense
  diningOut: IRetirementExpense
  travel: IRetirementExpense
  entertainment: IRetirementExpense
  clothing: IIndvidualRetirementExpense
  grooming: IIndvidualRetirementExpense
}

interface IPerson {
  firstName: string
  lastName: string
  dob: moment.Moment
  id: Guid,
  gender: Gender
}

interface IRetirementPerson extends IPerson {
  PIA: number
  selectedAgeExpectancy: number
  selectedRetirementAge: number
  WEPReduction: number
  govPensionForGPO: number
}

interface IFamily {
  members: IRetirementPerson[]
  headOfHousehold: Guid // of the member person
}

interface IRetirement {
  [guid: string]: IRetirementSavingsPersonInputs
}

const ageFixer = (p, propertyName: string = 'beginPaymentsAge') => {
  if (p && !moment.isDuration(p[propertyName])) { p[propertyName] = moment.duration(p[propertyName]) }
}

function makeDurationFixer(propertyName: string) {
  return (p: any) => {
    if (p) {
      if (p[propertyName]) {
        if (!moment.isDuration(p[propertyName])) { p[propertyName] = moment.duration(p[propertyName]) }
      } else {
        p[propertyName] = moment.duration(0)
      }
    }
  }
}

/**
 * Person class
 */
class Profile implements ISerializable<Profile> {

  /**
   * Returns new member (does not modify object)
   */
  public static createMember() {
    return {
      firstName: '',
      lastName: '',
      gender: Gender.unidentified,
      id: utils.newGuid(),
      PIA: 200,
      dob: moment().subtract(20, 'years').month(0).date(1),
      selectedAgeExpectancy: null,
      selectedRetirementAge: null,
      WEPReduction: 0,
      govPensionForGPO: 0
    }
  }

  /**
   * Make a new Profile object from some raw JSON.
   *
   * @param raw JSON input
   */
  static fromJSON(raw: any): Profile {
    return new Profile().deserialize(raw)
  }

  /**
   * Make a sample Profile object.
   */
  static makeFaux(): Profile {
    const r = new Profile()
    r.createdWhen = moment.utc('2017-08-01')
    r.modifiedWhen = moment.utc('2017-08-18')
    r.lastLoginWhen = moment.utc()
    return r
  }

  // when this account was created
  createdWhen: moment.Moment

  // when this account was last modified
  modifiedWhen: moment.Moment

  // when ths user last explicitly logged in
  lastLoginWhen: moment.Moment

  // when this account was cancelled
  cancellationWhen: moment.Moment

  // demographic information about the family
  family: IFamily

  // expense information might go here
  expenses?: IExpenses

  // SSA information goes here
  SSA?: ISSAInformation

  // rates of return
  ratesOfReturn?: IRatesOfReturn

  // Retirement information goes here
  retirement?: IRetirement

  // information related to all then pensions held
  pension: {
    spousal?: ISpousalPensionInputs,
    ncpers?: INcpersPensionInputs,
    ncpersPrior?: INcpersPriorPensionInputs,
    local145?: ILocal145PensionInputs,
    local697?: ILocal697PensionInputs,
    local701?: ILocal701PensionInputs,
    IO?: IIOPensionInputs
    NEBF?: INEBFPensionInputs,
    local705?: ILocal705PensionInputs,
    shared?: ISharedPensionInputs,
    fandf?: IFandfPensionInputs
  }

  privacyPolicyAcceptance: {
    isAccepted: boolean
    when?: moment.Moment
  }

  termsOfServiceAcceptance: {
    isAccepted: boolean
    when?: moment.Moment
  }

  showIntroduction?: boolean

  extraInfo: any

  constructor() {
    this.makeFamily()
    this.createdWhen = this.lastLoginWhen = moment.utc();
    this.retirement = {}
    this.pension = {
      shared: {
        hoursWorked: 2048
      }
    }
    this.privacyPolicyAcceptance = { isAccepted: false }
    this.termsOfServiceAcceptance = { isAccepted: false }
    this.extraInfo = {}
    this.SSA = { useOptimizedAges: false }
    this.ratesOfReturn = defaultRatesOfReturn
  }

  /**
   * Merge with another data source
   * @param data
   */
  mergeWith(data: any) {
    _.merge(this, data)
  }

  /**
   * Deserializes a blob of raw JSON into this object.
   *
   * @param raw JSON input
   */
  deserialize(raw: any): Profile {

    // pick up all the easy stuff
    this.mergeWith(raw)

    // process the more complex objects
    if (raw.createdWhen) { this.createdWhen = moment.utc(raw.createdWhen) }
    if (raw.modifiedWhen) { this.modifiedWhen = moment.utc(raw.modifiedWhen) }
    if (raw.lastLoginWhen) { this.lastLoginWhen = moment.utc(raw.lastLoginWhen) }
    if (raw.cancellationWhen) { this.cancellationWhen = moment.utc(raw.cancellationWhen) }

    if (raw.family && Array.isArray(raw.family.members)) {
      for (const member of this.family.members) {
        if (_.isEmpty(member.id)) {
          member.id = utils.newGuid()
        }

        if (typeof member.dob === 'string') {
          member.dob = moment.utc(member.dob)
        }

        if (isGoodMoment(member.dob) && (member.dob.year() > 2018)) { member.dob = undefined }
        if (member.selectedAgeExpectancy > 120) { member.selectedAgeExpectancy = 85 }
        if (member.selectedRetirementAge > 100) { member.selectedRetirementAge = 65 }
      }
    } else {
      this.makeFamily()
    }

    if (this.expenses) {
      if (this.expenses.auto && !_.isArray(this.expenses.auto)) {
        // some profiles have hashes for auto. Nuke them
        this.expenses.auto = []
      }
      makeDurationFixer('expenseSwitchoverMemberAge')(this.expenses)
      this.expenses.estimatedMonthlyExpensesPostSwitchover = this.expenses.estimatedMonthlyExpensesPostSwitchover || this.expenses.estimatedMonthlyExpenses
    }

    // RA-196 if this is a legacy user, give them some ratesOfReturn info
    if (!this.ratesOfReturn) { this.ratesOfReturn = defaultRatesOfReturn }

    if (this.retirement && typeof this.retirement === 'object') {
      const retirement: IRetirement = this.retirement
      const durationAttrs = ['annualContributions.stopWhen', 'withdrawals.startWhen']
      const nukeAttrs = ['fixedBalances.retirementAccounts']

      //Render ISO String Durations to moment.Durations
      Object.keys(retirement).forEach((key) => {
        const member = retirement[key]

        // RA-235/FF80 - bogus retirement values
        // tslint:disable-next-line:no-debugger
        nukeAttrs.forEach((attrName) => {
          _.unset(member, attrName)
        })

        durationAttrs.forEach((durationAttr) => {
          const durationISO = _.get(member, durationAttr)

          if (durationISO) {

            const duration = moment.duration(durationISO)
            if (0 === duration.asSeconds()) { // duration.invalid() seems missing...
              // this property is supposed to be a duration. If it's a moment, fix it.
              const when = moment.utc(durationISO)
              if (when.isValid()) {
                const age = moment.duration(when.diff(this.getMember(key).dob))
                // tslint:disable-next-line:no-console
                //console.log('deserialized and converted a moment from ' + durationISO + ' into a duration for ' + durationAttr + ' as ' + utils.toNiceAge(age))
                _.set(member, durationAttr, age)
              } else {
                _.set(member, durationAttr, moment.duration(65, 'years'))
              }
            } else {
              // tslint:disable-next-line:no-console
              //console.log('deserialized a duration from ' + durationISO + ' into a duration for ' + durationAttr + ' as ' + utils.toNiceAge(duration))
              _.set(member, durationAttr, duration)
            }
          } else {
            //_.set(member, durationAttr, moment.duration(65, 'years'))
          }
        })
      })

      //debugger
    }

    // clean up the member and spouse as needed
    ensureSaneProfile(this.getHeadOfHousehold())
    ensureSaneProfile(this.getSpouse())

    // wipe out some chaff in the profiles
    // tslint:disable-next-line:no-string-literal
    this['asdf'] = undefined
    // tslint:disable-next-line:no-string-literal
    this['ncpers'] = undefined
    // tslint:disable-next-line:no-string-literal
    this['ncpersPrior'] = undefined
    // tslint:disable-next-line:no-string-literal
    this['spouse'] = undefined

    if (this.pension) {

      if (this.pension.ncpers) {
        // tslint:disable-next-line:no-string-literal
        if (_.has(this.pension.ncpers, 'extraAgeToCalculate')) {
          // tslint:disable-next-line:no-string-literal
          this.pension.ncpers.beginPaymentsAge = moment.duration(this.pension.ncpers['extraAgeToCalculate'], 'years')
          // tslint:disable-next-line:no-string-literal
          this.pension.ncpers['extraAgeToCalculate'] = undefined
        }
      }

      if (this.pension.spousal) {
        if (typeof this.pension.spousal.ageSalaryStops === 'string') {
          this.pension.spousal.ageSalaryStops = moment.duration(this.pension.spousal.ageSalaryStops)
        }
        if (!moment.isDuration(this.pension.spousal.ageSalaryStops)) { this.pension.spousal.ageSalaryStops = moment.duration(this.pension.spousal.ageSalaryStops, 'years') }
      }

      ageFixer(this.pension.ncpers)
      ageFixer(this.pension.ncpersPrior)
      ageFixer(this.pension.spousal)
      ageFixer(this.pension.local701)
      ageFixer(this.pension.IO)
      ageFixer(this.pension.NEBF)
      ageFixer(this.pension.local145)
      ageFixer(this.pension.local705)
      ageFixer(this.pension.local697)
      ageFixer(this.pension.local697, 'planPPaymentsAge')
      ageFixer(this.pension.fandf)

      const fixBenefitServiceYears = makeDurationFixer('benefitServiceYears')
      fixBenefitServiceYears(this.pension.local705)

      makeDurationFixer('futureService')(this.pension.local701)
      makeDurationFixer('yearsOfAMembership')(this.pension.IO)
      makeDurationFixer('currentYearsService')(this.pension.NEBF)

      this.SSA = this.SSA || { useOptimizedAges: false }

      if (!this.pension.shared) {
        // setup shared pension values
        this.pension.shared = {
          hoursWorked: 2048
        }
      }

      // if the profile doesn't have any 'showIntroduction' property, then it must
      // be an old one. In this case, we show the introduction page
      if (_.isUndefined(this.showIntroduction)) {
        this.showIntroduction = true
      }

      if ((configName === '705') && this.pension.local705) {
        if (_.isUndefined(this.pension.local705.employerContribution)) {
          ETL705.migrate1p4To2Spreadsheet(this);
        }
      }

      if ((configName === 'fandf') && this.pension.fandf) {
        this.pension.fandf.hireDate = moment.utc(this.pension.fandf.hireDate)
      }

      this.mergeExtraInfo(this.extraInfo)

    } else {
      this.pension = {}
    }

    return this;
  }

  memberPIAMissing() {
    return personPIAMissing(this.getHeadOfHousehold())
  }

  mergeExtraInfo(extraInfo: any) {
    this.extraInfo = extraInfo || {}

    if ((configName === 'fandf') && this.pension.fandf) {
      ETLFandf.loadUpgradeableData(this)
    }

    if ((configName === '697') && this.pension.local697) {
      ETL697.loadUpgradeableData(this)
    }

    if ((configName === '701') && this.pension.local701) {
      ETL701.loadUpgradeableData(this)
    }

    if ((configName === '705') && this.pension.local705) {
      ETL705.loadUpgradeableData(this)
    }

    if (configName === '71') {
      ETL71.loadUpgradeableData(this)
    }

    if (configName === '145') {
      ETL145.loadUpgradeableData(this)
    }
  }

  familyNamesAndAges(when: moment.Moment) {
    const x = _(this.family.members)
      .filter((p) => isAlive(p, when))
      .map((p) => {
        const age = ageAtAsDuration(p, when)
        return memberNameAndAge(p, age)
      })
      .value()
    return x.join(', ')
  }

  /**
   * Get Head of household
   */
  getHeadOfHousehold(): IRetirementPerson {
    const headGuid = this.family.headOfHousehold || this.family.members[0].id

    if (headGuid) {
      const head = this.family.members.find((m) => m.id === headGuid)

      if (head) { return head }
    }

    return this.family.members[0]
  }

  getBetterHalf(half: IRetirementPerson): IRetirementPerson {
    const member = this.getHeadOfHousehold()
    return half.id === member.id ? this.getSpouse() : member
  }

  getFirstRetirement(people: IRetirementPerson[] = null) {
    people = people || this.getImportantPeopleWhoPlannedRetirement()
    return _(people).orderBy((p) => retirementDate(p).unix()).first()
  }

  getImportantPeople(): IRetirementPerson[] {
    const r = _.filter(this.family.members, (p) => isValidPerson(p))
    return r
  }

  getImportantPeopleWhoPlannedRetirement(): IRetirementPerson[] {
    const r = _.filter(this.family.members, (p) => isValidPerson(p) && (0 !== p.selectedRetirementAge))
    return r
  }

  getPeopleForSSA(): IRetirementPerson[] {
    const folks: IRetirementPerson[] = []
    const member = this.getHeadOfHousehold()
    if (isValidPersonForSSA(member)) { folks.push(member) }
    const spouse = this.getSpouse()
    if (isValidPersonForSSA(spouse)) { folks.push(spouse) }
    return folks
  }

  getRetirementForHeadOfHouseHold() {
    const head = this.getHeadOfHousehold()

    return this.getRetirementFor(head.id)
  }

  getRetirementForSpouse() {
    const spouse = this.getSpouse()

    return this.getRetirementFor(spouse.id)
  }

  getRetirementFor(memberId: string): IRetirementSavingsPersonInputs {
    if (memberId in this.retirement) {
      return ensureRetirementSavingsPersonInputs(this.retirement[memberId])
    }

    // tslint:disable-next-line:no-console
    console.log('initializing retirement info for member: ' + memberId)
    this.retirement[memberId] = ensureRetirementSavingsPersonInputs(undefined)
    return this.retirement[memberId]
  }

  getLastDeathDate(): moment.Moment {
    let dd = moment(0)
    let lastPersonAlive: IRetirementPerson
    this.getImportantPeople().forEach((p) => {
      const death = deadBy(p)
      if (death.isAfter(dd)) { dd = death, lastPersonAlive = p }
    })

    // tslint:disable-next-line:no-console
    //console.info(lastPersonAlive.firstName + ' dies last (by ' + dd.format('LLL') + ')')

    return dd
  }

  getMember(id: string): IRetirementPerson {
    return this.family.members.find((m) => m.id === id)
  }

  /**
   * Get spouse (or first non-head of household member)
   */
  getSpouse(): IRetirementPerson {
    const headGuid = this.family.headOfHousehold

    if (headGuid) {
      const headIndex = this.family.members.findIndex((m) => m.id === headGuid)

      if (headIndex >= 0) {
        const spouse = this.family.members.find((m, i) => i !== headIndex)

        if (spouse) { return spouse }
      }
    }

    return this.family.members[1]
  }

  /**
   * Adds head of household and spouse
   */
  makeFamily() {
    const head = Profile.createMember()
    const spouse = Profile.createMember()
    spouse.dob = undefined

    this.family = {
      headOfHousehold: head.id,
      members: [head, spouse]
    }
  }

  resetSection(what: SectionType) {
    // tslint:disable-next-line:no-console
    console.log('nuke profile: ' + what)
    switch (what) {
      case 'basic':
        this.makeFamily()
        break

      case 'retirement':
        this.retirement = {}
        break

      case 'pension':
        this.pension = {}
        break

      case 'expenses':
        this.expenses = undefined
        break

      default:
        throw new Error('nukeProfile- Unknown SectionType mode: ' + what)
    }
  }

  spouseIsValid() {
    return isValidPerson(this.getSpouse())
  }

  spousePIAMissing() {
    return personPIAMissing(this.getSpouse())
  }

  /**
   * Cleans up Autos that have been removed
   */
  cleanUpAutoExpenses() {
    if (this.expenses && Array.isArray(this.expenses.auto)) {
      this.expenses.auto = this.expenses.auto.filter((vehicle) => {
        if (!vehicle.name) { return false }
        return Object.keys(vehicle).some((key) => {
          const value = vehicle[key]

          // tslint:disable-next-line:no-console
          console.log('auto cleanup of ' + JSON.stringify(vehicle) + ', key: ' + key + ', val: ' + (typeof value) + ' -- ' + _.isNumber(value))
          return _.isNumber(value)
        })
      })
    }
  }

  /**
   * Cleans up a user who is being removed
   * @param person - the person being cleaned up
   */
  cleanUpPerson(person: IRetirementPerson, cleanDemographicToo: boolean = true) {
    personalExpenseFieldNames.forEach((f) => {
      if (this.expenses && this.expenses[f] && this.expenses[f][person.id]) {
        delete this.expenses[f][person.id]
      }
    })

    // wack the retirement data too
    delete this.retirement[person.id];

    // and if it's the spouse, remove spousal pension
    if (person === this.getSpouse()) {
      delete this.pension.spousal;
    }

    if (cleanDemographicToo) {
      person.firstName = ''
      person.lastName = ''
      person.PIA = 0
      person.selectedAgeExpectancy = 0;
      person.selectedRetirementAge = null
      delete person.dob
    }
  }
}

/**
 * Ensures that a person exists-- or throws an error.
 *
 * @param p - the person that is required.
 */
const assertPerson = (person: IPerson) => {
  if (!isValidPerson(person)) {
    throw new Error('assertPerson -- No (or invalid) person found!')
  }
}

function isSamePersonForSSA(a: IRetirementPerson, b: IRetirementPerson): boolean {
  if (!a || !b) { return false }

  const dob = a.dob.isSame(b.dob)
  const retirementAge = a.selectedRetirementAge === b.selectedRetirementAge
  const lifeExpectancy = a.selectedAgeExpectancy === b.selectedAgeExpectancy
  const pia = a.PIA === b.PIA
  return dob && retirementAge && lifeExpectancy && pia
}

function ageAt(person: IPerson, when: moment.Moment): number {
  return when.diff(person.dob, 'years')
}

function ageAtAsDuration(person: IPerson, when: moment.Moment): moment.Duration {
  return moment.duration(when.diff(person.dob))
}

function ageToDuration(age: number): moment.Duration {
  return moment.duration(age, 'years')
}

function ensureSaneProfile(person: IRetirementPerson) {
  if (person) {
    if (!person.selectedAgeExpectancy) { person.selectedAgeExpectancy = 85 }
  }
}

function clonePerson(person: IPerson): IPerson {
  return {
    firstName: person.firstName,
    lastName: person.lastName,
    dob: person.dob ? person.dob.clone() : undefined,
    id: person.id,
    gender: person.gender
  }
}

function cloneRetirementPerson(person: IRetirementPerson): IRetirementPerson {
  return _.merge(clonePerson(person), {
    PIA: person.PIA,
    selectedAgeExpectancy: person.selectedAgeExpectancy,
    selectedRetirementAge: person.selectedRetirementAge,
    WEPReduction: person.WEPReduction,
    govPensionForGPO: person.govPensionForGPO
  })
}

function dateAtAge(person: IPerson, age: moment.Duration) {
  assertPerson(person)
  return person.dob.clone().add(age)
}

function deadBy(person: IRetirementPerson): moment.Moment {
  return person.dob.clone().add(person.selectedAgeExpectancy, 'years')
}

function expectedDeathYear(person: IRetirementPerson): number {
  return deadBy(person).year()
}

function getEstimatedExpenseSwitchoverDate(profile: Profile) {
  const member = profile.getHeadOfHousehold()
  assertPerson(member)
  return member.dob.clone().add(profile.expenses.expenseSwitchoverMemberAge)
}

function hasDOB(person: IPerson): boolean {
  if (!person) { return false }
  return isGoodMoment(person.dob)
}

function hasLifeExpectancy(person: IRetirementPerson): boolean {
  return !!person.selectedAgeExpectancy
}

function hasRetirementAge(person: IRetirementPerson): boolean {
  return !!person.selectedRetirementAge
}

function isAlive(person: IRetirementPerson, when: moment.Moment): boolean {
  if (!person.dob) { return false }
  return deadBy(person).isAfter(when)
}

function isValidPerson(person: IPerson): boolean {
  return !_.isUndefined(person) && !_.isUndefined(person.firstName) && (0 !== person.firstName.length) && hasDOB(person)
}

function isValidPersonForSSA(person: IRetirementPerson): boolean {
  if (!isValidPerson(person)) { return false }

  // if the person has a PIA, then they must also have a retirement age
  if ((0 !== person.PIA) && !person.selectedRetirementAge) {
    // tslint:disable-next-line:no-console
    //console.log('Person "' + person.firstName + '" has a PIA: ' + person.PIA + ', but NO selectedRetirementAge!')
    return false
  }

  // they're all good
  return true
}

function lastSurvivor(people: IRetirementPerson[]): IRetirementPerson {
  let dd = moment(0)
  let lastPersonAlive: IRetirementPerson
  people.forEach((p) => {
    const death = deadBy(p)
    if (death.isAfter(dd)) { dd = death, lastPersonAlive = p }
  })

  // tslint:disable-next-line:no-console
  //console.info(lastPersonAlive.firstName + ' dies last (by ' + dd.format('LLL') + ')')

  return lastPersonAlive
}

function memberNameAndAge(person: IRetirementPerson, age: moment.Duration): string {
  return person.firstName + ' is ' + utils.toNiceAge(age)
}

type zoo = keyof IRetirementPerson
type VerifiablePersonProperties = 'dob' | 'selectedAgeExpectancy' | 'selectedRetirementAge' | 'PIA'
type xxx = zoo & VerifiablePersonProperties
type PropertyVerifiers = { [key in xxx]: (person: IRetirementPerson) => boolean }
const allPropertyVerifiers: PropertyVerifiers = {
  dob: hasDOB,
  selectedAgeExpectancy: hasLifeExpectancy,
  selectedRetirementAge: hasRetirementAge,
  PIA: (x) => false,
}

function missingPersonProperties(person: IRetirementPerson, neededProperties: VerifiablePersonProperties[]): string[] {
  return _.reduce(neededProperties, (acc, propName) => {
    const verifier = allPropertyVerifiers[propName]
    if (!verifier(person)) { acc.push(propName) }
    return acc
  }, [])
}

function missingPeopleProperties(people: IRetirementPerson[], neededProperties: VerifiablePersonProperties[]): string[] {
  return _.reduce(people, (acc, person) => {
    const q = missingPersonProperties(person, neededProperties)
    if (q.length) {
      acc.push({ who: person, missing: q })
    }
    return acc
  }, [])
}

function peopleAreSameForSSA(a: IRetirementPerson[], b: IRetirementPerson[]) {
  if (!a || !b) { return false }
  if (a.length < 2 || b.length < 2) { return false }
  return isSamePersonForSSA(a[0], b[0]) && isSamePersonForSSA(a[1], b[1])
}

function personHasRetired(person: IRetirementPerson, byWhen: moment.Moment = utils.todaysDate()): boolean {
  return byWhen.isAfter(retirementDate(person))
}

function personPIAMissing(person: IRetirementPerson) {
  return !_.isUndefined(person) && (0 === person!.PIA)
}

function retirementDate(person: IRetirementPerson): moment.Moment {
  if (!person || !person.dob) {
    // tslint:disable-next-line:no-debugger
    debugger
  }
  return person.dob.clone().add(person.selectedRetirementAge, 'years')
}

function retirementYear(person: IRetirementPerson): number {
  if (!person || !person.dob) {
    // tslint:disable-next-line:no-debugger
    debugger
  }
  return person.dob.clone().add(person.selectedRetirementAge, 'years').year()
}

function updateLifeExpectancy(person: IRetirementPerson): Promise<IRetirementPerson> {
  return LifeExpectancyService.getLifeExpectancy(person)
    .then((newLifeExpectancy) => {
      person.selectedAgeExpectancy = newLifeExpectancy
      return person
    })
}

export {
  AutoOwnership,
  IAutoExpense,
  RetirementUnits,
  SectionType,
  VerifiablePersonProperties,
  Gender,
  Guid,
  IExpenses,
  IFamily,
  IPerson,
  Profile,
  IRetirementPerson,
  ageAt,
  ageAtAsDuration,
  ageToDuration,
  assertPerson,
  clonePerson,
  cloneRetirementPerson,
  dateAtAge,
  deadBy,
  ensureSaneProfile,
  expectedDeathYear,
  getEstimatedExpenseSwitchoverDate,
  hasDOB,
  hasLifeExpectancy,
  hasRetirementAge,
  isAlive,
  isSamePersonForSSA,
  isValidPerson,
  isValidPersonForSSA,
  lastSurvivor,
  memberNameAndAge,
  missingPersonProperties,
  missingPeopleProperties,
  peopleAreSameForSSA,
  personHasRetired,
  personPIAMissing,
  retirementDate,
  retirementYear,
  updateLifeExpectancy
}



// WEBPACK FOOTER //
// ./src/app/redux/Profile/model/index.ts